import type { ClinicalNote } from '@/components/ClinicalNotes';

export type TimeWindow = 'admission' | '24h' | '48h' | '7d' | 'all';
export type SortOrder = 'newest' | 'oldest';

export interface NoteFilter {
  type: string; // 'all' or an exact noteType
  window: TimeWindow;
  sort: SortOrder;
  admissionStart?: string;
}

const WINDOW_MS: Record<string, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '48h': 48 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

function timeOf(note: ClinicalNote): number {
  const t = new Date(note.createdAt).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function deMarkdownSnippet(text: string, maxLines = 2): string {
  const lines = (text || '').split('\n');
  if (lines.length && /^\s*#{1,6}\s/.test(lines[0])) lines.shift();
  const cleaned = lines
    .map((l) =>
      l
        .replace(/^\s*#{1,6}\s+/, '')
        .replace(/^\s*[-*+]\s+/, '')
        .replace(/^\s*\d+\.\s+/, '')
        .replace(/^\s*>\s?/, '')
        .replace(/[*_`]+/g, '')
        .trim(),
    )
    .filter((l) => l.length > 0);
  return cleaned.slice(0, maxLines).join(' ');
}

export function noteTypeOptions(notes: ClinicalNote[]): string[] {
  return Array.from(new Set(notes.map((n) => n.noteType).filter(Boolean))).sort();
}

export function getAdmissionStart(
  encounters?: Array<{ start?: string; class?: string }>,
): string | undefined {
  if (!encounters || encounters.length === 0) return undefined;
  const withStart = encounters.filter((e) => e.start);
  if (withStart.length === 0) return undefined;
  const inpatient = withStart.filter((e) => /inpatient|\bimp\b|icu|emer/i.test(e.class || ''));
  const pool = inpatient.length ? inpatient : withStart;
  return pool.reduce((latest, e) =>
    new Date(e.start as string).getTime() > new Date(latest.start as string).getTime() ? e : latest,
  ).start;
}

export function filterAndSortNotes(notes: ClinicalNote[], filter: NoteFilter): ClinicalNote[] {
  let result = notes.slice();

  if (filter.type && filter.type !== 'all') {
    const t = filter.type.toLowerCase();
    result = result.filter((n) => (n.noteType || '').toLowerCase() === t);
  }

  if (filter.window === 'admission' && filter.admissionStart) {
    const start = new Date(filter.admissionStart).getTime();
    result = result.filter((n) => timeOf(n) >= start);
  } else if (WINDOW_MS[filter.window]) {
    const times = notes.map(timeOf).filter((t) => t > 0);
    const reference = times.length ? Math.max(...times) : 0;
    const cutoff = reference - WINDOW_MS[filter.window];
    result = result.filter((n) => timeOf(n) >= cutoff);
  }

  result.sort((a, b) => (filter.sort === 'oldest' ? timeOf(a) - timeOf(b) : timeOf(b) - timeOf(a)));
  return result;
}
